import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { CreatableCombobox } from '../../../components/common/CreatableCombobox';
import { type CreateGreenBeanProfileDTO } from '../../../types/greenBean';
import { useLanguage } from '../../../features/language/useLanguage';

const PROCESS_TYPES = ['Washed', 'Honey', 'Natural', 'Other'];

interface GreenBeanProcessingSectionProps {
  uniqueOptions: {
    processors: string[];
  };
  disabled?: boolean;
}

export const GreenBeanProcessingSection: React.FC<GreenBeanProcessingSectionProps> = ({ uniqueOptions, disabled = false }) => {
  const { t } = useLanguage();
  const { control, register } = useFormContext<{ green_bean_profile: CreateGreenBeanProfileDTO }>();

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-medium text-gray-900 border-b pb-2">{t('greenBean.processing.title')}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Controller
            control={control}
            name="green_bean_profile.processor"
            render={({ field }) => (
              <CreatableCombobox
                label={t('greenBean.processing.processorName')}
                value={field.value}
                onChange={field.onChange}
                options={uniqueOptions.processors}
                placeholder={t('greenBean.extrinsic.placeholderProcessor')}
                dataTestId="green-bean-processor-input"
                disabled={disabled}
              />
            )}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('inventory.modal.fields.processType')}</label>
          <select
            disabled={disabled}
            {...register('green_bean_profile.process_type')}
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 ${disabled ? 'bg-gray-100' : ''}`}
            data-test-id="green-bean-process-type-select"
          >
            <option value="">{t('greenBean.processing.selectProcessType')}</option>
            {PROCESS_TYPES.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('greenBean.extrinsic.wetMill')}</label>
          <input
            type="text"
            disabled={disabled}
            {...register('green_bean_profile.wet_mill')}
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 ${disabled ? 'bg-gray-100' : ''}`}
            data-test-id="green-bean-wet-mill-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('greenBean.extrinsic.dryMill')}</label>
          <input
            type="text"
            disabled={disabled}
            {...register('green_bean_profile.dry_mill')}
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 ${disabled ? 'bg-gray-100' : ''}`}
            data-test-id="green-bean-dry-mill-input"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700">{t('greenBean.processing.otherDetails')}</label>
          <input
            type="text"
            disabled={disabled}
            {...register('green_bean_profile.processing_other')}
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 ${disabled ? 'bg-gray-100' : ''}`}
            data-test-id="green-bean-processing-other-input"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700">{t('greenBean.processing.notes')}</label>
          <textarea
            disabled={disabled}
            {...register('green_bean_profile.processing_notes')}
            rows={3}
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 ${disabled ? 'bg-gray-100' : ''}`}
            data-test-id="green-bean-processing-notes-input"
          />
        </div>
      </div>
    </div>
  );
};
